// Turning the free-text `venue` string into WHO is putting an event on.
//
// There is no organiser column, and `host` is not one either - it holds the KIND
// of event. The closest thing the data has is the venue, which for most rows IS
// the business running it. Dropping the trailing address gets most of the way
// there, and for a long time that was the whole rule.
//
// It isn't enough, because the venue column is typed by hand, once per event,
// by whoever loaded that week's issue. Three failures showed up in the 7/28
// audit of all 449 approved rows, and each one is a different bug:
//
//   1. SOME ROWS CARRY ONLY AN ADDRESS. "607 Madison St" is five Clarity House
//      events, "920 Commerce St Ste 107" is two Hammer & Stain workshops. The
//      leading-digit guard dropped them, which was right - nobody follows a
//      street number - but it meant a venue's own events went missing from its
//      count. And the same guard threw out 801 Raw Bar on Rivermont, a business
//      whose NAME starts with a number.
//
//   2. ONE PLACE, SEVERAL SPELLINGS, SEVERAL CHECKBOXES. Apocalypse Ale Works
//      rendered twice (bare, and "(Forest)"), which reads as two breweries;
//      there is only one, at 1257 Burnbridge Rd. Templeton was three spellings,
//      Dish was two, Three Roads was three. Splitting a venue across boxes is
//      the exact failure lib/hosts.ts exists to stop on the category column.
//
//   3. SOME VENUE STRINGS NAME WHERE PEOPLE GATHER, NOT WHO GATHERED THEM.
//      "Meets at the Community Market" is a walking tour's meeting point; the
//      market is not putting it on. "Downtown" was the same shape of problem -
//      it named a part of town and collided with the Downtown neighborhood in
//      the Location section right below - and it is now the counter-example:
//      it stayed hostless for a day until Andrew named the operator (7/29).
//
// WHY A DISPLAY MAP RATHER THAN FIXING THE COLUMN. Same argument as
// lib/hostLabels.ts: the venue string is also read for the address on the submit
// form and for the Location section, so rewriting 449 rows changes three
// readers at once. The durable fix is still to normalize what gets typed in;
// this map is what keeps the drawer honest in the meantime, and it keeps working
// when the next issue introduces a fourth spelling of Templeton.
//
// WHAT THIS DELIBERATELY DOES NOT DO IS GUESS. An entry only appears below when
// the event names themselves name the business (all five rows at 607 Madison St
// are titled "The Clarity House: ..."), when a source confirmed it, or when
// Andrew answered outright. Where none of those held the rows stayed out of the
// list and went on a flagged list for him instead; the 7/29 pass is him coming
// back with those answers, and it emptied the list.
//
// A wrong host is worse than a missing one: it tells someone a business runs an
// event it has never heard of. That is worth pausing an instruction over: the
// Commerce Street Theater merge was asked for, held back with the evidence, and
// then settled the other way - it and the Music Hall both stay separate by
// Andrew's 7/29 call. The note in the Academy group below has the reasoning.

/**
 * Comparison key: case, edge space, inner runs of space - and, since 9/3, the
 * spellings of one name that a person types interchangeably.
 *
 * It used to be case and whitespace only, which meant ALIASES needed a separate
 * entry per SPELLING rather than per PLACE: "Beale's Brewery" and "Beale's
 * Brewery" (curly apostrophe) were two keys, so were "Sattva Yoga & Wellness
 * Center" and "Sattva Yoga and Wellness Center", and so were "Old Court House
 * Steps" and "...steps". Folding them here means one entry canonicalizes a whole
 * family, in EITHER direction - the entry's key and the variant both fold to the
 * same string, so writing the odd spelling on the left fixes the common one too.
 *
 * Deliberately NOT folded: a trailing town. "Downtown Bedford" would fold to
 * "Downtown", which is Hill City Tours in the map below, and quietly crediting a
 * tour company with somebody else's event is exactly the failure the file's
 * header calls worse than a missing host. Venues with a town welded onto the
 * name and no comma ("Hammer & Stain Lynchburg") get one explicit entry instead.
 */
function key(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/&/g, ' and ')
    .replace(/[.,'"()\-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^the /, '')
}

/**
 * Venue strings that name a place but not a HOST, so they list nobody.
 *
 * Empty as of 7/29, and kept anyway, because this is the holding pen and not a
 * mistake to clean up. A value lands here the day an audit finds it and leaves
 * the day somebody can say who runs the thing: both of its members - 'Downtown'
 * and 'Behind the Forest Library' - graduated into ALIASES below when Andrew
 * named the business. The next issue will introduce another one.
 *
 * It is checked against the raw venue AND against the canonicalized name, which
 * is the part the `meets at` rule in hostedByName cannot do on its own.
 *
 * Sitting here costs those events their Hosted by checkbox and nothing else:
 * they are still reachable by date, by category, by area and by search.
 */
const NOT_A_HOST = new Set<string>(
  [
    // A placeholder the feed writes when it could not read a venue. It reached
    // production as /venue/venue-not-named-in-feed/ on 9/4, which is the shape
    // of thing this pen exists for.
    '(venue not named in feed)',
    // A street, not a venue. Its four rows link to amherstcountyfair.com, so the
    // fairground is presumably on it, but naming it would be inventing a venue
    // name from an address - hold it out until someone can say what it is called.
    'Sweet Briar Ln, Amherst, VA',
  ].map(key),
)

/**
 * Spellings of one place, and the one name it should list under.
 *
 * Keyed on the venue string AFTER the trailing address is dropped, so a place
 * that appears both with and without its address needs one entry, not two.
 * Where a place has an official current name that differs from what got typed,
 * the official one wins: Parks & Rec renamed the Templeton Senior Center to
 * Templeton Center when it opened to adults of all ages, and 21 of its 24 rows
 * still carried the old name.
 */
const ALIAS_SOURCE: Record<string, string> = {
    // --- One brewery, not two. Verified: a single site at 1257 Burnbridge Rd,
    // Forest, and the beer garden is its patio.
    'Apocalypse Ale Works (Forest)': 'Apocalypse Ale Works',
    'Apocalypse Ale Works & Beer Garden, Forest': 'Apocalypse Ale Works',
    // --- 'DISH' and 'Dish' fold to the same key already; the fuller name is
    // 'Dish Restaurant', its own venue string on other rows, so both short
    // spellings now land there instead of on the bare 'Dish' checkbox.
    DISH: 'Dish Restaurant',
    // --- The definite article, typed half the time.
    'The Scrappy Elephant': 'Scrappy Elephant',
    Trailhead: 'The Trailhead',
    // --- Same club, and the LONGER name is the one to show (Andrew, 7/29): it
    // is what they call themselves on the Instagram both spellings link to.
    // This entry used to point the other way, so the direction is the change.
    'Gilded Dogwood Mahjong': 'The Gilded Dogwood Mahjong Society',
    // --- Renamed by its operator; the old name is still on most rows.
    'Templeton Senior Center': 'Templeton Center',
    'Templeton Center (Anna M. Filiaggi)': 'Templeton Center',
    // --- One taproom, three ways of writing its nickname. The brewery's own
    // site calls it the Lynchburg Taproom; it is the only Three Roads location
    // that appears here, so the plain name is unambiguous.
    'Three Roads Brewing - BURG': 'Three Roads Brewing',
    'Three Roads Brewing BURG': 'Three Roads Brewing',
    'Three Roads BURG': 'Three Roads Brewing',
    // A fourth spelling, and one the digit guard used to swallow outright - "3
    // Roads Brewing Co" starts with a digit, so without this entry it fell
    // through to null like a bare street number, and its 2 events had no host
    // at all. Same fix as 801 Raw Bar on Rivermont below: a digit-led business
    // name gets its own entry so the guard never sees it.
    '3 Roads Brewing Co': 'Three Roads Brewing',
    // --- A room and a person, inside places already listed.
    "Millie's Living Cafe patio, inside Health Nut Nutrition": "Millie's Living Cafe",
    'Lynchburg Amphitheater': 'Lynchburg Amphitheater at Riverfront Park',

    // --- ONE BUILDING ON GROVE ST, ONE CHECKBOX (Andrew, 7/29). Rooms, a room's
    // coordinator, the theatre inside it and the public library's branch inside
    // it were four checkboxes for 301 Grove St. The Library Hub is the one worth
    // spelling out: it reads like a separate library and is the biggest of the
    // four at 13 rows, but it is a desk in the Miller Center, and someone
    // scanning for where an event IS finds it under the building's name.
    //
    // Collapsing a name does not delete it. The event card and the popup render
    // the raw venue string, and venueSearchTerms returns both spellings, so
    // "Library Hub" still shows on the event and still finds it in the box.
    'Miller Center, Rm 207': 'Miller Center',
    'Miller Center (Nancy McAndrew)': 'Miller Center',
    'Miller Center Theater': 'Miller Center',
    'Miller Center Theater, Rm 205': 'Miller Center',
    'Library Hub at the Miller Center': 'Miller Center',
    // Five more spellings of the same building, this time with the room and the
    // Library Hub name run together with "@" or parentheses rather than a
    // comma, so `strippedVenue` never touches them - none of these starts with
    // a digit after the "@", and none is a comma tail. Explicit entries are the
    // only fix.
    'Miller Center Community Room -Room 102 @ Miller Center Library Hub': 'Miller Center',
    'Miller Center Library Hub (Miller Center Community Room -Room 102)': 'Miller Center',
    'Miller Center Library Hub (Miller Center Room 104)': 'Miller Center',
    'Miller Center Room 104 @ Miller Center Library Hub': 'Miller Center',
    'Miller Center Room 306 @ Miller Center Library Hub': 'Miller Center',
    // ⚠️ Miller Park is a park two miles away on Park Ave and the Miller-Claytor
    // House is a historic house in Riverside Park. Neither is the Miller Center.
    // The shared surname is the whole resemblance, so the keys above are written
    // out in full rather than matched on a "Miller" prefix.
    'Miller Park, Grove St side': 'Miller Park',

    // --- ONE ARTS CENTER, ONE CHECKBOX (Andrew, 7/29). The Academy runs the
    // Historic Academy Theatre, its galleries and the warehouse theatre off
    // Commerce St as one organisation, and all eleven rows link to
    // academycenter.org. Same consolation as the Miller Center above: the room
    // name stays on the event, so a ticket-holder still reads "Joy & Lynch
    // Christian Warehouse Theatre, 519 Commerce St" in the popup.
    'Historic Academy Theatre': 'Academy Center of the Arts',
    // Same room, spelled with the Academy's own name in front and "Theater"
    // rather than "Theatre".
    'Academy Center of the Arts Historic Theater': 'Academy Center of the Arts',
    'Academy galleries': 'Academy Center of the Arts',
    'Joy & Lynch Christian Warehouse Theatre': 'Academy Center of the Arts',
    "The Academy's Warehouse Theatre, off Commerce St": 'Academy Center of the Arts',
    'Warehouse Theatre (Academy Center), off Commerce St': 'Academy Center of the Arts',
    // ⚠️ TWO PLACES THAT LOOK LIKE THEY BELONG IN THIS GROUP AND DO NOT. Both
    // were put to Andrew on 7/29 with the evidence below; he decided SEPARATE on
    // both. Settled, not pending - don't re-open either without a new fact.
    //
    // 'Commerce Street Theater' was asked for in this group and is left out. It
    // is a separate community theatre - its own board and staff, its own site at
    // commercestreettheater.org, its own listing in the Downtown Lynchburg
    // business directory - at 1022 Commerce St, which is neither the Academy's
    // 600 Main St nor the warehouse's 519. Its one row links to
    // facebook.com/CSTLynchburg while every row above links to academycenter.org,
    // and the Academy's own venues page does not list it. The shared street name
    // is the whole resemblance, and folding it in would credit the Academy with
    // another company's show.
    //
    // 'The Music Hall' (722 Commerce St, 8 rows, its own checkbox) is the harder
    // one, and it also stays out. The evidence genuinely cuts both ways: the
    // Academy's venues page DOES list a Music Hall, and six of its eight rows
    // link to academycenter.org - but the Academy puts its spaces at 600 Main St
    // while these rows say 722 Commerce, and one of the eight is a Shining Star
    // Karaoke night. Andrew weighed that and chose separate. It has a working
    // checkbox at 8 rows, so unlike the two above this is a merge somebody would
    // actually SEE; that is the reason to leave it alone rather than tidy it.

    // --- Same library, 382 S Main St in Amherst, under two names.
    'Amherst Library': 'Amherst County Public Library',

    // --- ONE PLACE TYPED WITH ITS TOWN AND WITHOUT IT (Andrew, 9/3). Typing
    // "Bower" offered Bower Center for the Arts twice. That whole class -
    // Bower, Apocalypse, National D-Day, Peaks of Otter, Underground Cards,
    // Wood Ridge, the Forest Farmers Market, every Miller Center room number -
    // is gone STRUCTURALLY now: `strippedVenue` drops a trailing comma-clause
    // that says where a place is rather than what it is called. Fourteen
    // entries would have sat here; none of them has to.
    //
    // What still needs naming is a qualifier that is neither an address, a town
    // nor a room, because no rule can tell "Miller-Claytor House, Riverside
    // Park" (the park it stands in) from "Hailey Theatre, Dillard Fine Arts
    // Center" (a room the name needs) without being told.
    'Miller-Claytor House, Riverside Park': 'Miller-Claytor House',

    // --- ONE PLACE, TWO SPELLINGS OF ITS OWN NAME (Andrew, 9/3).
    //
    // `key()` folds case, curly apostrophes, "&"/"and", punctuation and a
    // leading "The", so ONE entry canonicalizes a whole family and it does not
    // matter which spelling is on the left - both fold to the same key and both
    // come back as the value. What the entry is really declaring is the winner.
    //
    // The winner is what the business calls ITSELF where that is knowable (the
    // Templeton rule), and the spelling most rows agree on otherwise.
    //
    // lynchburgartclub.org writes "and", not "&", in its logo and footer.
    'Lynchburg Art Club & Gallery': 'Lynchburg Art Club and Gallery',
    // thesuperradarcade.com titles itself "Super Rad Arcade Bar"; one row of
    // five dropped the last word.
    'Super Rad Arcade': 'Super Rad Arcade Bar',
    // charleyslynchburg.com is "Charley's Restaurant & Catering", which is also
    // the biggest of the three spellings at four rows. The short one's own link
    // is facebook.com/CharleysRestaurant.
    "Charley's": "Charley's Restaurant & Catering",
    "Charley's Restaurant": "Charley's Restaurant & Catering",
    // Same recurring storytime and LEGO club on lynchburg.librarycalendar.com.
    // ⚠️ A different key from 'Downtown' (Hill City Tours, below) and from
    // 'Downtown Branch Library'. Three keys, three meanings.
    'Downtown Branch': 'Downtown Branch Library',
    // The library's desk inside the Miller Center, written the other way round.
    // 'Library Hub at the Miller Center' is already in the group above; this is
    // the same room with the words swapped, which no fold can see.
    'Miller Center Library Hub': 'Miller Center',
    // A town welded onto the name with no comma, so `strippedVenue` cannot
    // reach it and `key` deliberately will not fold it (see key's note on
    // "Downtown Bedford"). One entry each is the honest fix.
    'Hammer & Stain Lynchburg': 'Hammer & Stain',
    'Visiting Angels Lynchburg': 'Visiting Angels',
    // Straight vs curly apostrophe. One row each against eleven, except
    // Benjamin's, which is 4 against 6 and was caught on 9/4 by the venue-page
    // slug-collision guard rather than by the search sweep - both spellings sit
    // beyond the window that sweep reads, so the guard is the only thing that
    // sees them.
    'Beale\u2019s Brewery': "Beale's Brewery",
    'Drifter\u2019s': "Drifter's",
    'Benjamin\u2019s Restaurant': "Benjamin's Restaurant",
    // "and" spelled out on two rows against nineteen.
    'Sattva Yoga and Wellness Center': 'Sattva Yoga & Wellness Center',
    // Case only, on the last word.
    'Old Court House steps': 'Old Court House Steps',
    // Case inside the word. Eight rows write the second V capital, one does not.
    // ⚠️ NOT the same place as Riverviews Artspace (60 rows, riverviews.org):
    // this is the record shop, and its rows link to Facebook and Instagram.
    'Riverview Vinyl': 'RiverView Vinyl',
    // One cemetery under five spellings, all six rows linking to gravegarden.org,
    // whose own name carries the ampersand. The chapel and the Lee Reading Room
    // are rooms inside it, folded the same way the Miller Center's are.
    'Old City Cemetery Museums and Arboretum': 'Old City Cemetery Museums & Arboretum',
    'Old City Cemetery': 'Old City Cemetery Museums & Arboretum',
    'Old City Cemetery Chapel': 'Old City Cemetery Museums & Arboretum',
    'Old City Cemetery, Lee Reading Room': 'Old City Cemetery Museums & Arboretum',
    // The hyphen the house's own name carries.
    'Miller Claytor House': 'Miller-Claytor House',

    // --- ONE PLACE UNDER A LONGER AND A SHORTER NAME (Andrew, 9/4). Surfaced by
    // the venue pages, which give each canonical name a URL and so make a split
    // visible as two pages rather than two checkboxes. The search sweep cannot
    // see these: they differ by whole words, not by case or punctuation.
    //
    // A typo, not a spelling: 14 rows say Restaurant, 8 say Resturant, all on
    // destinationbedfordva.com.
    'Mitchell\u2019s Resturant & Pizzeria': "Mitchell's Restaurant & Pizzeria",
    // 102 rows to 4, same booking domain.
    'Needle Ninja Sewing Studio': 'Needle Ninja',
    // 10 rows to 6, both on nelson-county-events.com.
    "Basic Necessities Cafe\u2019 Wine and Cheese Shop": 'Basic Necessities',
    // Peaks of Otter is where the farm is, not part of its name.
    'Johnson Farm, Peaks of Otter': 'Johnson Farm',
    // "- Nelson County" is a location tag the other four rows omit. An en dash,
    // so the comma rule never reaches it.
    'Three Notch\u2019d Brewery, Distillery & Craft Kitchen \u2013 Nelson County':
      "Three Notch'd Brewery, Distillery & Craft Kitchen",
    // poplarforest.org calls it Thomas Jefferson's Poplar Forest; five rows off
    // the tourism feed shorten it.
    'Poplar Forest': "Thomas Jefferson's Poplar Forest",
    // Andrew's 7/29 call was that the LONGER name wins here. That settled two
    // spellings; this is the third, and the one with the most rows.
    'The Gilded Dogwood': 'The Gilded Dogwood Mahjong Society',
    // A leading "The" that some rows keep and others drop. The majority spelling
    // wins each one: forty-five rows say "The Clarity House", five say "The
    // Overlook", two say "Historic Bragassa Toy Store" with no article.
    'Clarity House': 'The Clarity House',
    'Overlook at Riverside Park': 'The Overlook at Riverside Park',
    'The Historic Bragassa Toy Store': 'Historic Bragassa Toy Store',
    // Both rows are the same Wolfbane gala at 723 Jefferson St.
    'Wolf Den - Downtown': 'The Wolf Den Downtown',
    // ⚠️ TWO THAT LOOK LIKE THEY BELONG HERE AND ARE LEFT OUT, both flagged to
    // Andrew on 9/3 rather than decided here.
    //
    // 'Miller Park Aviary' is not folded into 'Miller Park'. The aviary is its
    // own facility with its own Parks & Rec registration (the row books through
    // valynchburgweb.myvscloud.com/webtrac), the way the Commerce Street Theater
    // and the Music Hall stayed out of the Academy group above. Sharing a park's
    // name is not the same as being the park.
    //
    // 'Amherst' - one row, "Amherst Running Club Run", venue typed as the bare
    // town - is NOT a spelling of 'Amherst County Public Library' or of
    // 'Amherst County Visitor Center', so there is nothing to merge it into. It
    // is the 'Downtown' problem instead: a town name that also names an Area
    // filter, offering itself as a Place. The event title names the operator
    // outright, so 'Amherst': 'Amherst Running Club' is the shape of the answer -
    // but keying an alias on a bare town is the one move this file warns about
    // twice, and it is Andrew's call, not an audit's.
    // --- A building chain rather than a name. The business is the university.
    'Hailey Theatre, Dillard Fine Arts Center, University of Lynchburg':
      'Hailey Theatre (University of Lynchburg)',

    // --- Rows that carry ONLY an address. Each is matched to its business by
    // the event names on it, which say so outright.
    '607 Madison St': 'The Clarity House', // "The Clarity House: ..." x5
    '920 Commerce St Ste 107': 'Hammer & Stain', // "Hammer & Stain: ..." x2
    '1300 Main St': 'Starr Hill On Main', // "Starr Hill On Main: Gabriel Van Duo"
    '918 Commerce St': 'Mythic Hall', // "Mythic Hall: Learn to Be a Dungeon Master"
    '1219 Main St': 'Lynchburg Community Market', // "Lynchburg Community Market" x2

    // --- A business whose name opens with a number, which the guard below would
    // otherwise read as a street address and drop. Five karaoke nights.
    '801 Raw Bar on Rivermont': '801 Raw Bar on Rivermont',

    // --- One van, eight parking spots. The venue on these rows is where the
    // Bookmobile PARKS, which is worth keeping (you need to know the corner),
    // but it meant eight library events listed no host at all. The header
    // comment above says an entry needs the event names to say so outright OR a
    // source to confirm it; this is the second kind. The rows' own `link` column
    // points at lynchburgpubliclibrary.org/bookmobile/, and that page names
    // these exact stops in its Week A / Week B pilot rotation. It is the CITY
    // library, not the county system.
    '1100 Jefferson St': 'Lynchburg Public Library Bookmobile', // Riverfront Park
    '1721 Monsview Pl': 'Lynchburg Public Library Bookmobile', // Armstrong Place
    '317 Norwood St': 'Lynchburg Public Library Bookmobile', // Daniel's Hill
    '2238 Rivermont Ave': 'Lynchburg Public Library Bookmobile', // Riverside Park
    '3000 Birchwood Dr': 'Lynchburg Public Library Bookmobile', // Hillcrest Senior Apts
    '2154 Wards Rd': 'Lynchburg Public Library Bookmobile', // Wards Rd
    '1209 Greenview Dr, Mobil lot': 'Lynchburg Public Library Bookmobile', // Greenview
    'Fairview Heights Neighborhood Center': 'Lynchburg Public Library Bookmobile',
    // Bookmobile stop, not an ice cream shop event - the two spellings a
    // Bruster's parking-lot stop got typed under.
    "Bruster's of Lynchburg": 'Lynchburg Public Library Bookmobile',
    "Bruster's Real Ice Cream": 'Lynchburg Public Library Bookmobile', // 7815 Timberlake Rd
    // ⚠️ The Bookmobile ALSO stops at the Lynchburg Humane Society and at Miller
    // Park, and neither belongs in the list above. Adding 'Lynchburg Humane
    // Society' would be actively wrong: ALIASES is consulted on the venue with
    // its trailing address STRIPPED, and "Cat Yoga!" and "Blood Drive with the
    // Blood Connection" both carry 'Lynchburg Humane Society, 1211 Old Graves
    // Mill Rd', which strips to that same key - so the shelter's own events
    // would silently re-host to the library. Checked 7/29 across all ten
    // Bookmobile venue strings, exactly and after stripping; the eight above are
    // used by Bookmobile rows and nothing else. Miller Park keeps its own alias.

    // --- VENUE STRINGS THAT ARE NOT A NAME AT ALL, answered by Andrew (7/29).
    // Both of these sat in NOT_A_HOST until he said who was behind them, and the
    // rows' own `link` column agrees with him in each case.
    //
    // The ten ghost walks: every one points at hillcityafterdark.com, whose
    // waiver reads "HILL CITY AFTER DARK LLC DBA: Hill City Tours", so the DBA is
    // the name to show. The earlier audit read the three tour titles - Echoes on
    // the Avenue, Ghosts of Gallows Hill, Haunted Pub Crawl - as three operators;
    // they are three products of one company.
    //
    // ⚠️ 'Downtown' is the only alias here keyed on a word that is not a name,
    // and it holds ONLY because those ten rows are its only carriers today,
    // checked exactly and after stripping ('Downtown Branch Library' is a
    // different key and is untouched). An event downtown that is NOT a Hill City
    // tour has to get its venue set at the source: arriving here it would list
    // them as the host, silently and wrongly. Re-run scripts/hosted-by-check.ts
    // before trusting this one.
    Downtown: 'Hill City Tours',
    // Both rows are titled "Forest Farmers Market" and link to
    // forestfarmersmarket.com. This bought the market its name in SEARCH when it
    // was two rows and under the drawer's 3-row threshold; folding in the three
    // 'Forest Farmers Market, Forest' rows on 9/3 took it to five, so it now
    // earns the checkbox as well.
    'Behind the Forest Library': 'Forest Farmers Market',

    // --- Two names for the same downtown armory. Neither "downtown
    // Lynchburg" nor "City" is a name a stripping rule can see, so this maps
    // straight to the fuller string's own label rather than trying to shorten
    // either one.
    'City Armory': 'Armory, downtown Lynchburg',

    // --- ONE STADIUM, TWO NAMES. The city's own name for it is the Event
    // Complex; the teams that play there are named in the other spelling.
    'Lynchburg City Stadium Event Complex': 'City Stadium',
    'Hillcats/Howlers Stadium': 'City Stadium',

    // --- The restaurant's full name on one row, its short name on the rest.
    'Tiger Sun Restaurant & Lounge': 'Tiger Sun',

    // --- Short name for the same wellness studio. 'Everwell Integrative
    // Wellness, Forest' already folds to 'Everwell Integrative Wellness' on
    // its own, since Forest is a town strippedVenue already knows to drop.
    'Everwell Wellness': 'Everwell Integrative Wellness',

    // --- EXACT STRING ONLY. "Village of Lovingston, Lovingston" would
    // otherwise strip its repeated town down to just "Village of Lovingston",
    // losing "Farmers Market" entirely rather than finding it - there's no
    // "Farmers Market" anywhere in that string for a stripping rule to land
    // on. This entry is keyed on the one literal spelling that needs it; a
    // bare "Village of Lovingston" elsewhere is left alone rather than folded
    // into the market by a looser rule.
    'Village of Lovingston, Lovingston': 'Village of Lovingston Farmers Market',
}

const ALIASES: Record<string, string> = Object.fromEntries(
  Object.entries(ALIAS_SOURCE).map(([from, to]) => [key(from), to]),
)

/**
 * Entries that fold to the same key but disagree about the answer.
 *
 * Since `key()` started folding spellings (9/3), two entries written for what
 * looked like different strings can land on one key, and `Object.fromEntries`
 * resolves that by keeping whichever came LAST - silently, which is the whole
 * problem. A map whose job is to stop a place splitting in two must not be able
 * to lose an entry off the bottom of itself, so the eval asserts this is empty.
 *
 * Entries that fold together and AGREE are fine and expected: writing both
 * spellings of a name on the left is how a family gets documented.
 */
export function aliasKeyCollisions(): string[] {
  const byKey = new Map<string, Map<string, string[]>>()
  for (const [from, to] of Object.entries(ALIAS_SOURCE)) {
    const k = key(from)
    const answers = byKey.get(k) ?? new Map<string, string[]>()
    answers.set(to, [...(answers.get(to) ?? []), from])
    byKey.set(k, answers)
  }
  return [...byKey.entries()]
    .filter(([, answers]) => answers.size > 1)
    .map(
      ([k, answers]) =>
        `"${k}" <- ${[...answers.entries()]
          .map(([to, froms]) => `${froms.map((f) => `"${f}"`).join(', ')} => "${to}"`)
          .join('  AND  ')}`,
    )
}

/**
 * A second name to list an event under, layered ON TOP of its venue rather
 * than replacing it - ALIASES above swaps one name for another; this ADDS
 * one. Keyed on the venue's own canonical name (post-ALIASES, from
 * venueName()) plus a pattern over the EVENT's own name, because the venue
 * string alone can't say who's running it: "Palmera House" hosts Hill City
 * Mahj's game nights the same as it hosts everything else it rents its room
 * to, and only the event titles say which rows are theirs.
 *
 * Evidence-only, same rule as ALIASES: an entry lands here only when
 * something outside the venue string says so. Hill City Mahj is that case -
 * Andrew asked repeatedly (8/20) why searching their name found nothing,
 * their events are titled "Tile Tuesday: Open Play" and "Morning Mahj" at
 * Palmera House and "Tennis & Tiles: Mahjong 101, 102 & Open Play" at Oakwood
 * Country Club, venue strings that never mention them, and the tier prices he
 * forwarded from their DM are the evidence they're a real, separate business
 * running its own series at more than one venue.
 *
 * Both conditions are ANDed, which is what keeps this narrow: a "Morning
 * Mahj" event somewhere OTHER than Palmera House never picks up Palmera by
 * name-pattern alone, and a Palmera House or Oakwood event that isn't one of
 * theirs never picks up Hill City Mahj by venue alone. Patterns stay anchored
 * to Hill City Mahj's own titles rather than a bare /mahjong/ - The Gilded
 * Dogwood Mahjong Society and the Templeton senior mahjong group are
 * different hosts, and a loose pattern would sweep their events in too.
 */
const ORGANIZERS: { venue: string; name: RegExp; organizer: string }[] = [
  { venue: key('Palmera House'), name: /^(tile tuesday|morning mahj)/i, organizer: 'Hill City Mahj' },
  { venue: key('Oakwood Country Club'), name: /^tennis & tiles/i, organizer: 'Hill City Mahj' },
]

/**
 * The venue with its trailing street address dropped and its spelling
 * canonicalized. Null when the venue string is an address and nothing else.
 *
 * Order matters: the map is consulted on the full string first, because the
 * address-only rows ARE the whole string, then again on the string with its
 * trailing address dropped, which is where every spelling variant lands.
 *
 * This is the naming half of the file, split out from `hostedByName` because
 * SEARCH wants it and hosting doesn't: "Downtown" is a place you can type into
 * the box even though it lists nobody as a host.
 */
export function venueName(venue: string | null): string | null {
  if (!venue) return null

  const whole = ALIASES[key(venue)]
  if (whole) return whole

  const name = strippedVenue(venue)
  if (!name) return null

  const stripped = ALIASES[key(name)]
  if (stripped) return stripped

  // A venue that is only a street address is not somewhere you can follow. The
  // businesses that legitimately start with a number are in the map above and
  // have already returned by now.
  if (/^\d/.test(name)) return null

  return name
}

/**
 * "Lola's, 1214 Main St" -> "Lola's". A trailing comma-clause comes off when it
 * says WHERE the place is rather than WHAT it is called: a street address, a
 * town, a room, or a "near/inside/behind ..." landmark.
 *
 * It was digit-led only until 9/3, on the stated grounds that the town in
 * "Underground Cards & Disc Shop, Forest" tells two shops apart. It doesn't -
 * there is one Underground Cards, at 1219 Burnbridge Rd Suite A - and the rule
 * was instead splitting fourteen venues across two dropdown rows apiece,
 * Bower Center for the Arts being the one Andrew hit (9/3). Checked before
 * changing it: across all 2550 approved rows, no cluster this merges joins two
 * genuinely different addresses, and the one pair that looked like it did was
 * 723 Jefferson St written two ways.
 *
 * A DIGIT-led clause still comes off unconditionally, and `venueAddress` still
 * reads only digit-led clauses - Dragon Fire Games really does have two
 * addresses, and that stays a display question, not a naming one.
 *
 * The list is closed on purpose. A clause that is neither an address nor one of
 * these stays part of the name, because "Hailey Theatre, Dillard Fine Arts
 * Center" is a room the name needs and "Peaks of Otter Winery, Bedford" is not.
 */
const TOWN_TAIL = new RegExp(
  `^(?:${[
    'Lynchburg', 'Forest', 'Bedford', 'Amherst', 'Appomattox', 'Madison Heights',
    'Rustburg', 'Altavista', 'Concord', 'Monroe', 'Evington', 'Gladys', 'Lovingston',
    'Big Island', 'Goode', 'Moneta', 'Huddleston', 'Nellysford', 'Buena Vista',
    'Wingina', 'Arrington', 'Faber', 'Shipman', 'Clifford', 'Sweet Briar',
    // The state, because "Bower Center for the Arts, Bedford, VA" stopped at
    // ", VA" and became a THIRD spelling of a venue that already had two.
    'VA', 'Virginia',
  ].join('|')})$`,
  'i',
)
// A room, a suite, or a landmark you'd be told to walk toward.
const PLACE_TAIL =
  /^(?:rm|room|ste|suite|unit|near|inside|behind|next to|across from|at the)\b|\b(?:rm|room|ste|suite|unit)\.?\s*\d/i

function strippedVenue(venue: string): string {
  let out = venue
    .replace(/[\u2018\u2019]/g, "'")
    // "The Music Hall @ 722 Commerce Street" - an address introduced by @ rather
    // than a comma. Same rule as the comma form: a digit-led run after the
    // separator is where the name stops.
    .replace(/\s+@\s*\d.*$/, '')
    .replace(/,\s*\d.*$/, '')
    .trim()
  // Repeated, because a venue can carry both ("Miller Center, Rm 104, Bedford").
  for (;;) {
    const m = out.match(/^(.*),\s*([^,]+)$/)
    if (!m) return out
    const tail = m[2].trim()
    if (!TOWN_TAIL.test(tail) && !PLACE_TAIL.test(tail)) return out
    // Never strip a place down to nothing.
    if (!m[1].trim()) return out
    out = m[1].trim()
  }
}

/**
 * Who puts an event on, or null when the venue string doesn't say.
 */
export function hostedByName(venue: string | null): string | null {
  if (!venue) return null
  if (NOT_A_HOST.has(key(venue))) return null

  const name = venueName(venue)
  if (!name) return null
  if (NOT_A_HOST.has(key(name))) return null

  // "Meets at X" names where to gather, not who is running it - these are bike
  // rides and walking tours whose operator is nowhere in the row.
  if (/^meets at\b/i.test(name)) return null

  return name
}

/**
 * Every name an event should list under: hostedByName's single answer, plus
 * any ORGANIZERS entry the event's venue and title match. Plural sibling of
 * venueSearchTerms below - same reason, an event can honestly belong under
 * more than one name - so a "Tile Tuesday" row at Palmera House lists under
 * BOTH names, not a choice between them.
 *
 * hostedByName keeps returning a single answer for callers that only ever
 * wanted one (scripts/hosted-by-check.ts, the search-eval fixtures); this is
 * what the drawer's Hosted by list, its counts, the ?hosted= slug filter and
 * the search suggestions all consume instead.
 */
export function hostedByNames(venue: string | null, eventName: string): string[] {
  const primary = hostedByName(venue)
  const out = primary ? [primary] : []
  const canonicalVenue = venueName(venue)
  if (canonicalVenue) {
    const venueKey = key(canonicalVenue)
    for (const rule of ORGANIZERS) {
      if (rule.venue === venueKey && rule.name.test(eventName) && !out.includes(rule.organizer)) {
        out.push(rule.organizer)
      }
    }
  }
  return out
}

/**
 * The venue text worth SEARCHING - names only, never the address.
 *
 * Typing "1219" or "Main St" used to pull up every event on that block, because
 * the matcher ran over the raw venue string and half those strings end in a
 * street address (Andrew, 7/29). Nobody searches a calendar by house number;
 * what they type is a name. So the address comes off here, and rows that are
 * ONLY an address contribute the business they belong to ("607 Madison St" ->
 * The Clarity House) or nothing at all.
 *
 * Both spellings come back where they differ, so a row stored as "Templeton
 * Senior Center" is found by that AND by the name it now lists under.
 */
export function venueSearchTerms(venue: string | null): string[] {
  if (!venue) return []
  const canonical = venueName(venue)
  if (!canonical) return []
  const raw = strippedVenue(venue)
  if (/^\d/.test(raw) || key(raw) === key(canonical)) return [canonical]
  return [raw, canonical]
}

/**
 * The street address inside a venue string, for DISPLAY beside a place's name.
 *
 * Context, never a match key: it tells someone which Dragon Fire Games they're
 * about to pick, and that's all it's for (see `venueSearchTerms`).
 */
export function venueAddress(venue: string | null): string | null {
  if (!venue) return null
  const trailing = venue.match(/,\s*(\d.*)$/)
  if (trailing) return tidyAddress(trailing[1])

  // Rows that carry only an address: the whole string is it. A business whose
  // name opens with a number maps to ITSELF in the alias map, which is what
  // tells 801 Raw Bar apart from 918 Commerce St.
  const trimmed = venue.trim()
  if (/^\d/.test(trimmed) && ALIASES[key(trimmed)] !== trimmed) return tidyAddress(trimmed)
  return null
}

/**
 * Drop the state and ZIP a few venue strings carry. The city is shown beside
 * the address already, so "305 N. Bridge St., Bedford, VA 24523" would spend a
 * suggestion row saying Bedford twice.
 */
function tidyAddress(addr: string): string | null {
  const out = addr
    .replace(/,\s*(VA|Virginia)\b\.?(\s*\d{5}(-\d{4})?)?\s*$/i, '')
    .replace(/[,\s]+$/, '')
    .trim()
  return out || null
}
